import React, { useState } from "react";
import { Link } from "react-router-dom";
import RatingScale from "./RatingScale";
import BoostDialog from "./BoostDialog";
import BoostCountdown from "./BoostCountdown";
import ReportDialog from "./ReportDialog";
import { fileUrl, formatApiError } from "../lib/api";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MessageCircle, Star, Rocket, Sparkles, Crown, Flag } from "lucide-react";

const TIER_ICON = { pod: Sparkles, elite: Crown };

export default function PostCard({ post, onUpdate }) {
    const { user } = useAuth();
    const [myRating, setMyRating] = useState(post.my_rating || null);
    const [avg, setAvg] = useState(post.avg_rating || 0);
    const [count, setCount] = useState(post.rating_count || 0);
    const [submitting, setSubmitting] = useState(false);
    const [boostOpen, setBoostOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    const isOwn = user && user.id === post.user_id;

    const rate = async (n) => {
        if (!user) {
            toast.error("Log in to rate");
            return;
        }
        if (isOwn) {
            toast.error("Can't rate your own post");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/posts/${post.id}/rate`, { rating: n });
            setMyRating(data.my_rating);
            setAvg(data.avg_rating);
            setCount(data.rating_count);
            onUpdate?.(data);
            toast.success(`Rated ${n}/10`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const isPremiumBoost = post.boost_type === "premium_boost";
    const highlightCls = post.boosted && isPremiumBoost ? "ring-4 ring-[#FFE55C] ring-offset-0" : "";

    return (
        <>
            <article
                className={`bg-white brut-static rounded-xl overflow-hidden fade-up ${highlightCls}`}
                data-testid={`post-card-${post.id}`}
            >
                {post.boosted && (
                    <div className="flex items-center gap-1.5 px-4 py-1.5 bg-[#FFE55C] border-b-2 border-[#0A0A0A] text-xs font-black uppercase tracking-wider" data-testid={`post-boosted-${post.id}`}>
                        <Rocket className="w-3.5 h-3.5" /> {isPremiumBoost ? "Premium Boost" : "Boosted"}
                        {post.boost_expires_at && (
                            <span className="ml-auto normal-case tracking-normal font-medium text-[#0A0A0A]/70">
                                ends in <BoostCountdown expiresAt={post.boost_expires_at} testId={`boost-countdown-${post.id}`} />
                            </span>
                        )}
                    </div>
                )}
                <header className="flex items-center justify-between px-4 py-3 border-b-2 border-[#0A0A0A]">
                    <Link to={`/u/${post.username}`} className="flex items-center gap-2" data-testid={`post-user-${post.id}`}>
                        <div className="w-9 h-9 rounded-full bg-[#B8E1FF] border-2 border-[#0A0A0A] flex items-center justify-center font-black text-sm">
                            {post.username?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div>
                            <div className="font-bold text-sm">@{post.username}</div>
                            <div className="text-xs text-[#4A4A4A]">{new Date(post.created_at).toLocaleDateString()}</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-1 bg-[#FFE55C] px-3 py-1 rounded-full border-2 border-[#0A0A0A]" data-testid={`post-avg-${post.id}`}>
                        <Star className="w-3.5 h-3.5 fill-[#0A0A0A]" />
                        <span className="font-black text-sm">{avg.toFixed(1)}</span>
                        <span className="text-xs font-medium">({count})</span>
                    </div>
                </header>

                <Link to={`/p/${post.id}`}>
                    <img
                        src={fileUrl(post.image_path)}
                        alt={post.caption || "post"}
                        className="w-full max-h-[70vh] object-cover border-b-2 border-[#0A0A0A] bg-[#FFFDF7]"
                        loading="lazy"
                        data-testid={`post-img-${post.id}`}
                    />
                </Link>

                {post.caption && (
                    <div className="px-4 pt-3 text-sm font-medium" data-testid={`post-caption-${post.id}`}>
                        {post.caption}
                    </div>
                )}

                <div className="px-4 py-4">
                    {isOwn ? (
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-sm text-[#4A4A4A] font-medium italic">Your post · {count} rating{count === 1 ? "" : "s"}</div>
                            {!post.boosted && (
                                <button
                                    onClick={() => setBoostOpen(true)}
                                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg brut bg-[#B8E1FF] font-bold text-xs"
                                    data-testid={`boost-open-${post.id}`}
                                >
                                    <Rocket className="w-3.5 h-3.5" /> Boost
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] mb-2">
                                {myRating ? `You rated ${myRating}/10 — tap to change` : "Rate this 1 to 10"}
                            </div>
                            <RatingScale
                                value={myRating}
                                onChange={rate}
                                disabled={submitting}
                                size="sm"
                                testIdPrefix={`rating-${post.id}`}
                            />
                        </>
                    )}
                    <Link
                        to={`/p/${post.id}`}
                        className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#0A0A0A] hover:underline"
                        data-testid={`post-comments-link-${post.id}`}
                    >
                        <MessageCircle className="w-4 h-4" /> Comments
                    </Link>
                    {user && !isOwn && (
                        <button
                            onClick={() => setReportOpen(true)}
                            className="ml-3 mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#4A4A4A] hover:text-[#FF5A5F]"
                            data-testid={`post-report-${post.id}`}
                        >
                            <Flag className="w-3.5 h-3.5" /> Report
                        </button>
                    )}
                </div>
            </article>
            {boostOpen && <BoostDialog postId={post.id} onClose={() => setBoostOpen(false)} />}
            {reportOpen && <ReportDialog postId={post.id} onClose={() => setReportOpen(false)} />}
        </>
    );
}
